import type {
  AmenityFieldDto,
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  UserPropertyMultiEditRequestWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export const getDataToSave = ({
  inputs,
  categorizedAmenitiesKeys,
  listingAlias,
  fieldName,
  valueToSave,
}: {
  inputs: Record<string, boolean | string>;
  categorizedAmenitiesKeys: Record<string, Array<string>>;
  listingAlias: string;
  fieldName?: string;
  valueToSave?: string | boolean;
}): UserPropertyMultiEditRequestWebDto => {
  const property = Object.keys(inputs).reduce(
    (a: Record<string, boolean | string>, key) => {
      if (categorizedAmenitiesKeys[key]) {
        categorizedAmenitiesKeys[key].forEach((unique) => (a[unique] = unique === inputs[key]));
      } else {
        a[key] = inputs[key];
      }

      return a;
    },
    { listingAlias },
  );

  if (fieldName) {
    /**
     * if `fieldName` is a `category`, then `valueToSave` is an `apiKeyValue` of such `category`,
     * else `fieldName` is `apiKey` and `valueToSave` is `boolean`
     */
    if (categorizedAmenitiesKeys[fieldName]) {
      categorizedAmenitiesKeys[fieldName].forEach((categoryKey) => (property[categoryKey] = false));
      property[valueToSave as string] = true;
    } else {
      property[fieldName] = valueToSave as boolean;
    }
  }

  return {
    property: {
      ...property,
      listingAlias,
    },
  };
};

export const getAiSelectedFieldNames = (aiAmenityFields?: Array<AmenityFieldDto>): Set<string> =>
  new Set(aiAmenityFields?.filter(({ value }) => value).map(({ field }) => field));

export const hasAiSelectionsInGroups = (
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>,
  aiAmenityFields?: Array<AmenityFieldDto>,
): boolean => {
  const aiSelected = getAiSelectedFieldNames(aiAmenityFields);
  return groups.some(({ values }) => values.some(({ fieldName }) => aiSelected.has(fieldName)));
};

export const resetAiAmenitySelections = ({
  inputs,
  groups,
  aiAmenityFields,
}: {
  inputs: Record<string, boolean | string>;
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  aiAmenityFields?: Array<AmenityFieldDto>;
}): Record<string, boolean | string> => {
  const aiSelected = getAiSelectedFieldNames(aiAmenityFields);
  const resetInputs = { ...inputs };

  groups.forEach(({ category, isUniqueSelection, values }) => {
    if (isUniqueSelection) {
      if (aiSelected.has(String(resetInputs[category]))) {
        resetInputs[category] = '';
      }
      return;
    }

    values.forEach(({ fieldName }) => {
      if (aiSelected.has(fieldName)) {
        resetInputs[fieldName] = false;
      }
    });
  });

  return resetInputs;
};
